import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Modal, Spin, Alert } from '../../../components';
import { CLOUD_TYPE } from '../../../constants';
import { PreflightMonitor } from '../../../components';
import { useGetCloudAccountByIdQuery, useDeployApplianceMutation, useLazyGetApplianceByIdQuery } from '../../../api';
import { useNotificationContext } from '../../../providers';
import { handleError } from '../../../lib/helpers';
import './ApplianceSetupModal.scss';
import { useTriggerApplianceDeployMutation } from '../../../api';
import { AWSApplianceSetup } from './AWSApplianceSetup/AWSApplianceSetup';
import { AzureApplianceSetup } from './AzureApplianceSetup/AzureApplianceSetup';
import { AzureLocalApplianceSetup } from './AzureLocalApplianceSetup/AzureLocalApplianceSetup';
import { GCPApplianceSetup } from './GCPApplianceSetup/GCPApplianceSetup';

export const ApplianceSetupModal = ({ open, onClose, cloudAccountId, isRedeploy = false }) => {
    const { notifySuccess, notifyError } = useNotificationContext();
    const [showPreflightModal, setShowPreflightModal] = useState(false);
    const [appliancePayload, setAppliancePayload] = useState(null);
    const [savedApplianceData, setSavedApplianceData] = useState(null);
    const [isSavingConfig, setIsSavingConfig] = useState(false);
    const [deployAppliance] = useDeployApplianceMutation();
    const [getApplianceById] = useLazyGetApplianceByIdQuery();
    const [triggerApplianceDeploy, { isLoading: isLaunching }] = useTriggerApplianceDeployMutation();
    const isDeploying = isSavingConfig || isLaunching;

    const {
        currentData: cloudAccount,
        isFetching,
        error: fetchError,
    } = useGetCloudAccountByIdQuery(cloudAccountId, {
        skip: !open || !cloudAccountId,
    });

    useEffect(() => {
        if (open) {
            setShowPreflightModal(false);
            setAppliancePayload(null);
            setSavedApplianceData(null);
            setIsSavingConfig(false);
        }
    }, [open]);

    const handleSubmit = useCallback(
        async (payload) => {
            setIsSavingConfig(true);
            try {
                const result = await deployAppliance({ cloudAccountId, payload }).unwrap();
                const applianceId = result?.id || result?.appliance?.id;
                if (!applianceId) {
                    throw new Error('Appliance configuration was saved but the server did not return an appliance id.');
                }
                const applianceData = await getApplianceById({ applianceId }).unwrap().catch(() => ({ id: applianceId }));
                setAppliancePayload(payload);
                setSavedApplianceData(applianceData);
                setShowPreflightModal(true);
            } catch (err) {
                handleError(err, notifyError, 'Failed to save appliance configuration');
            } finally {
                setIsSavingConfig(false);
            }
        },
        [deployAppliance, getApplianceById, cloudAccountId, notifyError],
    );

    const handlePreflightComplete = useCallback(
        async (result) => {
            if (result.success && savedApplianceData?.id) {
                try {
                    await triggerApplianceDeploy({ applianceId: savedApplianceData.id }).unwrap();
                    setShowPreflightModal(false);
                    notifySuccess({
                        message: 'Success',
                        description: 'Appliance is now deploying',
                        duration: 5,
                    });
                    onClose(true);
                } catch (err) {
                    handleError(err, notifyError, 'Failed to start appliance deployment');
                    setShowPreflightModal(false);
                }
            }
        },
        [savedApplianceData, triggerApplianceDeploy, notifySuccess, notifyError, onClose],
    );

    const handlePreflightClose = useCallback(() => {
        setShowPreflightModal(false);
    }, []);

    const handleCancel = useCallback(() => {
        if (!isDeploying) {
            onClose(false);
        }
    }, [isDeploying, onClose]);

    const formContent = useMemo(() => {
        if (!cloudAccount) {
            return null;
        }

        const commonProps = {
            cloudAccount,
            onSubmit: handleSubmit,
            onCancel: handleCancel,
            submitting: isDeploying,
            isRedeploy,
        };

        switch (cloudAccount.type) {
            case CLOUD_TYPE.AWS:
                return <AWSApplianceSetup {...commonProps} />;
            case CLOUD_TYPE.AZURE:
                return <AzureApplianceSetup {...commonProps} />;
            case CLOUD_TYPE.AZURE_LOCAL:
                return <AzureLocalApplianceSetup {...commonProps} />;
            case CLOUD_TYPE.GCP:
                return <GCPApplianceSetup {...commonProps} />;
            default:
                return (
                    <Alert
                        title="Unsupported Cloud Type"
                        description={`Appliance setup is not supported for ${cloudAccount.type} cloud accounts.`}
                        type="warning"
                        showIcon
                    />
                );
        }
    }, [cloudAccount, handleSubmit, handleCancel, isDeploying, isRedeploy]);

    const preflightMigrationContext = useMemo(() => ({
        alternateName: 'Migration Appliance',
        ignoreValidationErrors: appliancePayload?.ignore_validation_errors || false,
    }), [appliancePayload?.ignore_validation_errors]);

    const preflightRequestsPayload = useMemo(() => {
        if (savedApplianceData?.id) {
            return [
                {
                    type: 'appliance',
                    resource_id: savedApplianceData.id,
                    sourceData: savedApplianceData,
                    overrides: {},
                },
            ];
        }
        return [];
    }, [savedApplianceData]);

    const modalTitle = useMemo(() => {
        const action = isRedeploy ? 'Redeploy' : 'Setup';
        return `${action} Migration Appliance - ${cloudAccount?.name}`;
    }, [isRedeploy, cloudAccount]);

    return (
        <>
            <Modal
                title={modalTitle}
                open={open && !showPreflightModal}
                onCancel={handleCancel}
                footer={null}
                width={900}
                className="appliance-setup-modal"
                closable={!isDeploying}
                maskClosable={!isDeploying}
                destroyOnHidden={true}
            >
                <div className="appliance-setup-modal-content">
                    {isFetching && !cloudAccount && (
                        <div className="appliance-setup-modal__spinner">
                            <Spin size="large" />
                        </div>
                    )}
                    {fetchError && (
                        <Alert
                            message="Error"
                            description={fetchError.message || 'Failed to load cloud account details'}
                            type="error"
                            showIcon
                        />
                    )}
                    {!fetchError && formContent}
                </div>
            </Modal>

            {/* Preflight Checks Modal (Step 2) */}
            {showPreflightModal && (
                <PreflightMonitor
                    open={showPreflightModal}
                    onClose={handlePreflightClose}
                    onComplete={handlePreflightComplete}
                    preflightRequests={preflightRequestsPayload}
                    title="Check Migration Appliance Configuration"
                    loadingMessage="Initiating Migration Appliance checks..."
                    continueButtonText={'Launch Migration Appliance Instance'}
                    proceedOnSuccess={true}
                    migrationContext={preflightMigrationContext}
                    inspectSourcesMode={false}
                    hideCsvDownload={true}
                />
            )}
        </>
    );
};

